<template>
  <v-card class="mx-auto pa-12 pb-8" elevation="8" rounded="lg">
    <div class="d-flex py-3 justify-space-between">
      <v-list-item class="w-100">
        <slot name="header-slot">
          <h1>{{ prettyName }}</h1>
        </slot>

        <div v-if="showSelectors" class="selectors">
          <ParentSelectorsV2
            v-if="isNew"
            :model-class="modelClass"
            :is-form="true"
          />
          <slot name="selectors" />
        </div>

        <div v-if="modelLoaded" class="w-100">
          <FormBuilder
            ref="formBuilder"
            :form="formState"
            :config="config"
            @loading="setLoading"
            @error="handleError"
          >
            <template #default>
              <slot name="content" />
              <v-card
                v-if="!formValidation.isValid"
                class="mb-12"
                color="red-darken-1"
              >
                <v-card-text
                  v-for="(
                    validationErrorMessage, index
                  ) in formValidation.errorMessages"
                  :key="index"
                  class="text-medium-emphasis text-caption"
                >
                  <div class="text-white text-left">
                    {{ validationErrorMessage }}
                  </div>
                </v-card-text>
              </v-card>

              <v-card v-if="hasError" class="mb-12" color="red-darken-1">
                <v-card-text class="text-medium-emphasis text-caption">
                  <div class="text-white text-left">{{ errorMessage }}</div>
                </v-card-text>
              </v-card>

              <v-card
                v-if="props.warningMessages.length > 0"
                class="mb-12"
                color="yellow-darken-1"
              >
                <v-card-text
                  v-for="(warningMessage, index) in props.warningMessages"
                  :key="index"
                  class="text-medium-emphasis text-caption"
                >
                  <div class="text-white text-left">{{ warningMessage }}</div>
                </v-card-text>
              </v-card>
              <v-divider></v-divider>
            </template>
            <template #form-submit>
              <v-list-item>
                <v-btn
                  color="primary"
                  class="me-4"
                  prepend-icon="mdi-content-save"
                  :disabled="!formValid || !editable"
                  @click="save"
                  >{{ $t('components.common.save') }}</v-btn
                >
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-close"
                  @click="navigateWhenCompleted"
                  >{{ $t('components.common.cancel') }}</v-btn
                >
              </v-list-item>
            </template>
          </FormBuilder>
        </div>
      </v-list-item>
    </div>
    <LoadingOverlay v-model:loading="formState.loading" :persistent="true" />
  </v-card>
</template>

<script setup lang="ts">
import _ from 'lodash'
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSnackbar } from 'vuetify-use-dialog'

import { apiClientV2 } from '@/api/ApiClientV2'
import errorHandler from '@/api/ErrorHandler'
import { clientAppRouteName } from '@/apps/clientAppRegistry'
import {
  getClientAppOfRoute,
  getLocationPreservingState,
  routeHasClientApp,
} from '@/apps/routingUtils'
import ParentSelectorsV2 from '@/components/common/lists/ParentSelectorsV2.vue'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import { TransientBaseObjectType } from '@/models/core/base'
import { insertPropsInObject } from '@/util/util'

import FormBuilder from './FormBuilder.vue'
import { FormBuilderConfig } from './formBuilderHelper'

type BaseFormV2Props = {
  modelClass: TransientBaseObjectType
  id: string
  columnWidth?: string
  hasError?: boolean
  errorMessage?: string
  beforeSaveHook?: Function
  afterSaveHook?: Function
  warningMessages?: string[]
  navigateOnSave?: boolean
  completedLocation?: {
    name: string
    params?: Record<string, string>
    replace?: boolean
  } | null
  customCreate?: Function | null
  editable?: boolean
  customConfig?: FormBuilderConfig | null
  showSelectors?: boolean
  goBack?: boolean
}

const router = useRouter()

const props = withDefaults(defineProps<BaseFormV2Props>(), {
  columnWidth: 'is-12',
  hasError: false,
  errorMessage: '',
  beforeSaveHook: undefined,
  afterSaveHook: undefined,
  warningMessages: () => [],
  navigateOnSave: true,
  completedLocation: null,
  customCreate: null,
  editable: true,
  goBack: false,
  customConfig: null,
  showSelectors: true,
})

const createSnackbar = useSnackbar()
const emit = defineEmits(['created', 'loaded', 'getobject'])

const { t } = useI18n()
const config: FormBuilderConfig =
  props.customConfig || props.modelClass.formConfig()
const objectType: string = props.modelClass.objectType

const formBuilder: Ref<typeof FormBuilder> = ref(null)

const originalDataObject: any = ref(null)
const formValidation = ref({
  isValid: true,
  errorMessages: [],
})
const modelLoaded: Ref<boolean> = ref(false)
const formState = ref({
  $valid: true,
  loading: false,
})

watch(
  () => props.id,
  () => {
    loadForm()
  }
)

onMounted(() => {
  loadForm()
})

function parseModel(model: object) {
  return props.modelClass.parseModel(model, props.modelClass.fields)
}

function setLoading(loading: boolean) {
  formState.value.loading = loading
}

async function loadForm() {
  if (isNew.value) {
    // New object, nothing else to do
    originalDataObject.value = parseModel(config.model)
    modelLoaded.value = true
  } else {
    formState.value.loading = true
    const id = props.id
    try {
      const response = await apiClientV2.get(props.modelClass, id)
      emit('getobject', response)
      let obj = Object.assign({}, response)
      originalDataObject.value = parseModel(obj)
      emit('loaded', originalDataObject.value)
      config.model = JSON.parse(JSON.stringify(originalDataObject.value))
      formState.value.loading = false
      modelLoaded.value = true
    } catch (error) {
      handleError(error)
    }
  }
}

const isNew = computed(() => {
  return props.id === '0'
})

async function navigateWhenCompleted() {
  if (props.goBack) {
    router.back()
    return
  }
  if (props.completedLocation !== null) {
    // a location was given via props
    try {
      // Add small delay to resolve any pending router navigations
      await new Promise((resolve) => setTimeout(resolve, 100))
      if (props.completedLocation.replace) {
        await router.replace(props.completedLocation)
      } else {
        const res = await router.push(props.completedLocation)
        if (res) {
          console.log(res)
        }
      }
    } catch (error) {
      console.error('Navigation error:', error)
    }
    return
  }

  // try to find a decent route
  if (routeHasClientApp(router.currentRoute.value)) {
    const clientApp = getClientAppOfRoute(router.currentRoute.value)
    const routeName = clientAppRouteName(
      clientApp.view_id,
      objectType + '-list'
    )
    const location = getLocationPreservingState(routeName, router)
    if (router.resolve(location).matched.length > 0) {
      // only go there if the location matches something
      try {
        const res = await router.push(location)
        if (res) {
          console.log(res)
        }
      } catch (error) {
        console.error('Navigation error:', error)
      }
      return
    }
  }

  // go somewhere at least
  router.back()
}

// TODO: Unit test this!
/**
 * Converts an objects like {'a.b': 1, d: 3} to {a: {b: 1}, d: 3}
 */
function parseDotString(model: object): object {
  let parsedModel = {}
  Object.keys(model).forEach((key) => {
    const keys = key.split('.')
    if (keys.length === 1 && key !== '') {
      parsedModel[keys[0]] = model[keys[0]]
    } else {
      if (parsedModel[keys[0]] === undefined) {
        parsedModel[keys[0]] = parseDotString({
          [keys.slice(1, keys.length).join('.')]: model[key],
        })
      } else {
        insertPropsInObject(
          parsedModel[keys[0]],
          parseDotString({
            [keys.slice(1, keys.length).join('.')]: model[key],
          })
        )
      }
    }
  })
  return parsedModel
}

async function save() {
  setLoading(true)
  // TODO: Use something better than 'any'
  const dataObject: any = parseDotString(formBuilder.value.model.value)

  if (props.modelClass) {
    // Remove 'virtual' fields (fields that are shown in form but do not belong to model itself)
    props.modelClass.fields.forEach((field) => {
      if (field.virtual && dataObject.hasOwnProperty(field.key)) {
        delete dataObject[field.key]
      }
      if (field.display === false || field.display === 'false') {
        // Field was not displayed, reset to original
        try {
          _.set(
            dataObject,
            field.key,
            _.get(originalDataObject.value, field.key)
          )
        } catch (err) {
          console.warn(err)
        }
      }
    })
  }

  try {
    if (props.beforeSaveHook !== undefined) {
      await props.beforeSaveHook(dataObject)
    }

    if (!dataObject.id || dataObject.id === '0' || dataObject.id === '') {
      if (props.customCreate !== null) {
        await props.customCreate(dataObject)
      } else {
        await apiClientV2.create(props.modelClass, dataObject)
      }
    } else {
      await apiClientV2.update(props.modelClass, dataObject)
    }
    originalDataObject.value = JSON.parse(JSON.stringify(dataObject))

    createSnackbar({
      text: t('components.common.saveSuccess'),
      snackbarProps: { color: 'green-darken-3' },
    })
    if (props.afterSaveHook !== undefined) {
      await props.afterSaveHook(dataObject)
    }

    if (isNew.value) {
      emit('created', dataObject)
    }

    if (props.navigateOnSave) {
      navigateWhenCompleted()
    }
  } catch (error) {
    handleError(error)
  }
  setLoading(false)
}

function clearError() {
  formValidation.value.errorMessages = []
  formValidation.value.isValid = true
}

function handleError(error) {
  formValidation.value.errorMessages = errorHandler.errorToStrings(error)
  formValidation.value.isValid = false
}

const formValid = computed(() => {
  return formState.value?.$valid
})

const isNewObject = computed(() => {
  return props.id === undefined || props.id === '0'
})

const prettyName = computed(() => {
  return props.modelClass.prettyName()
})

defineExpose({
  isNewObject,
  clearError,
})
</script>

<style lang="scss" scoped>
.validation-message {
  margin-top: 10px;
}
</style>

<style lang="scss">
/* not scoped */
.width-400 {
  width: 400px;
}
</style>
